import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { type Route, type PreloadingStrategy } from '@angular/router';
import { Observable, of } from 'rxjs';

type SupportedNetworkInformation = {
  effectiveType?: string;
  saveData?: boolean;
};

type NavigatorWithConnection = Navigator & {
  connection?: SupportedNetworkInformation;
  mozConnection?: SupportedNetworkInformation;
  webkitConnection?: SupportedNetworkInformation;
};

type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  cancelIdleCallback?: (handle: number) => void;
};

@Injectable({ providedIn: 'root' })
export class SelectivePreloadingStrategy implements PreloadingStrategy {
  private readonly platformId = inject(PLATFORM_ID);

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (!route.data?.['preload']) {
      return of(null);
    }

    if (!isPlatformBrowser(this.platformId)) {
      return of(null);
    }

    const connection = this.getConnection();

    if (connection?.saveData || this.isSlowConnection(connection?.effectiveType)) {
      return of(null);
    }

    return new Observable((subscriber) => {
      const idleWindow = window as WindowWithIdleCallback;
      let innerSubscription: { unsubscribe(): void } | undefined;

      const startPreload = () => {
        innerSubscription = load().subscribe(subscriber);
      };

      if (idleWindow.requestIdleCallback) {
        const idleCallbackId = idleWindow.requestIdleCallback(startPreload, { timeout: 2000 });

        return () => {
          idleWindow.cancelIdleCallback?.(idleCallbackId);
          innerSubscription?.unsubscribe();
        };
      }

      const timeoutId = window.setTimeout(startPreload, 0);

      return () => {
        window.clearTimeout(timeoutId);
        innerSubscription?.unsubscribe();
      };
    });
  }

  private getConnection(): SupportedNetworkInformation | undefined {
    const navigatorWithConnection = navigator as NavigatorWithConnection;

    return (
      navigatorWithConnection.connection ??
      navigatorWithConnection.mozConnection ??
      navigatorWithConnection.webkitConnection
    );
  }

  private isSlowConnection(effectiveType?: string): boolean {
    return effectiveType === 'slow-2g' || effectiveType === '2g' || effectiveType === '3g';
  }
}
